import * as ROUTES from '../common/routes';
import React, { useState } from 'react';
import Switch from '@mui/material/Switch';
import TextareaAutosize from 'react-textarea-autosize';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import {
  useTranslations,
  useDynamicTranslations,
} from '../common/hooks/useTranslations';

interface IProps {
  type?: string;
  className?: string;
  placeholder?: string;
  passedRef?: any;
  maxLength?: number;
  dataName?: string;
  parentName?: string;
  options?: any;
  isRemainingVisible?: boolean;
  value?: any;
  defaultValue?: any;
  lng?: any;
  hideSwitch?: boolean;
}

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      inputContainerStyle: {
        position: 'relative',
        border: 'gray solid 1px',
        background: COLORS.white,
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '10px 16px',
      },
      inputContainerTextStyle: {
        maxHeight: '40px',
        display: 'flex',
        alignItems: 'center',
      },
      textStyle: {
        width: '100%',
        background: 'transparent',
        resize: 'none',
        overflow: 'hidden',
        boxShadow: 'none !important',
        fontFamily: FONTS.alegreya,
        fontSize: '16px',
        lineHeight: '22px',
        border: 'none',
      },
      textAreaStyle: {
        minHeight: '44px',
      },
      remainingStyle: {
        position: 'absolute',
        right: 0,
        bottom: 0,
        textAlign: 'right',
        fontFamily: FONTS.alegreya,
        fontSize: '12px',
        color: COLORS.textGray,
        padding: '4px',
      },
    })
);

const InputComponent = ({
  type,
  className,
  placeholder,
  passedRef,
  maxLength,
  dataName,
  parentName,
  options,
  isRemainingVisible,
  value,
  defaultValue,
  lng,
  hideSwitch = false,
}: IProps): JSX.Element => {
  const classes = useStyles();
  const [currentInput, setInput] = useState(defaultValue || '');
  const [isSwitchChecked, setSwitch] = useState(options?.isSwitchChecked);

  const publicLabel = useDynamicTranslations('public', lng);
  const hiddenLabel = useDynamicTranslations('hidden', lng);

  const remainingLabel = useDynamicTranslations('remaining', lng);
  const detailanswerLabel = useDynamicTranslations('detailanswer', lng);

  const onChange = (event) => {
    setInput(event?.target?.value);
  };

  const remaining = maxLength - currentInput?.length;

  const label = isSwitchChecked ? publicLabel : hiddenLabel;
  const ariaLabel = { inputProps: { 'aria-label': label } };

  const placeholderLabel = useDynamicTranslations(placeholder, lng);

  const placeholderText = options?.isMandatory
    ? `${placeholderLabel}*`
    : placeholderLabel;

  const onSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSwitch(checked);
  };

  const isSwitchVisible = !hideSwitch && options?.isSwitchVisible;

  if (type === 'text') {
    return (
      <div
        className={clsx(
          classes.inputContainerStyle,
          classes.inputContainerTextStyle,
          className
        )}
      >
        <input
          className={classes.textStyle}
          placeholder={placeholderText}
          maxLength={maxLength}
          ref={passedRef}
          data-name={dataName}
          data-parent-name={parentName}
          data-show-public={isSwitchChecked}
          data-type={'text'}
          type="text"
          onChange={onChange}
          defaultValue={defaultValue}
        />
        {isRemainingVisible && (
          <div className={classes.remainingStyle}>{remaining}</div>
        )}
        {Boolean(isSwitchVisible) && (
          <Tooltip title={label} placement={'top-end'} arrow>
            <div className={classes.switchContainerStyle}>
              <Switch
                {...ariaLabel}
                onChange={onSwitchChange}
                checked={isSwitchChecked}
                disabled={options?.isSwitchDisabled}
              />
            </div>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div className={clsx(classes.inputContainerStyle, className)}>
      <TextareaAutosize
        className={clsx(classes.textStyle, classes.textAreaStyle)}
        placeholder={placeholder || detailanswerLabel}
        maxLength={maxLength}
        ref={passedRef}
        data-name={dataName}
        data-parent-name={parentName}
        onChange={onChange}
        defaultValue={value}
      />
      <div
        className={classes.remainingStyle}
      >{`${remainingLabel} ${remaining}`}</div>
    </div>
  );
};

export default InputComponent;
